/* eslint-disable no-console */
import React, { useEffect, useState } from "react";
import { Phone, CheckCircle2, AlertTriangle, Save, RefreshCw, Trash2, HelpCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * TelecmiChubCard — Settings card for TeleCMI CHUB credentials.
 *
 * Iter 133 (Apr 30, 2026). Every agent enters their own CHUB id + password
 * once. The backend verifies them against CHUB's `/v1/agentLogin`,
 * caches the returned 30-day token, and auto-refreshes it before expiry.
 *
 * This replaces the old PIOPIY SDK seeding flow. CHUB calls ring the
 * agent's TeleCMI softphone (desktop / mobile) — there's no browser
 * audio.
 *
 * Admins see a bonus "Team" list showing which agents are configured so
 * they can spot gaps at a glance.
 */

const TelecmiChubCard = () => {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chubId, setChubId] = useState("");
  const [chubPassword, setChubPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [adminList, setAdminList] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const fetchMe = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/telecmi/chub/my-credentials");
      setMe(data);
      if (data?.chub_id && !chubId) setChubId(data.chub_id);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdmin = async () => {
    try {
      const { data } = await api.get("/telecmi/chub/admin/all");
      setAdminList(data?.agents || []);
      setIsAdmin(true);
    } catch {
      // Not admin — hide admin panel silently.
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    fetchMe();
    fetchAdmin();
    // fetchMe/fetchAdmin are intentionally mount-only — running them once
    // is the desired behaviour. Adding them to deps would require wrapping
    // both in useCallback and ripple through the file for no benefit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSave = async () => {
    if (!chubId.trim() || !chubPassword) {
      toast.error("Enter both CHUB agent ID and password");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/telecmi/chub/save-credentials", {
        chub_id: chubId.trim(),
        chub_password: chubPassword,
      });
      toast.success(`CHUB verified for ${data?.name || "agent"} ✓`);
      setChubPassword(""); // clear from UI for security
      await fetchMe();
      await fetchAdmin();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CHUB save failed");
    } finally {
      setSaving(false);
    }
  };

  const onVerify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/telecmi/chub/verify");
      toast.success(`CHUB re-verified for ${data?.agent?.name || "agent"} ✓`);
      await fetchMe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CHUB verify failed");
    } finally {
      setVerifying(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm("Remove your CHUB credentials? You won't be able to place calls until re-added.")) return;
    try {
      await api.delete("/telecmi/chub/credentials");
      toast.success("CHUB credentials removed");
      setChubId("");
      setChubPassword("");
      await fetchMe();
      await fetchAdmin();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    }
  };

  return (
    <div className="bento-card" data-testid="telecmi-chub-card" id="telecmi">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
            <Phone size={18} className="text-emerald-600" />
          </div>
          <div>
            <h3 className="text-[13px] font-bold text-[--text-primary]">TeleCMI (CHUB) · Click-to-Call</h3>
            <p className="text-[11px] text-[--text-muted]">Ring your softphone, then bridge to the customer.</p>
          </div>
        </div>
        {loading ? null : me?.configured ? (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
            <CheckCircle2 size={12} /> Configured
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
            <AlertTriangle size={12} /> Not configured
          </span>
        )}
      </div>

      {/* How it works — short primer so agents know what to expect */}
      <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-600 leading-relaxed">
        <div className="flex items-start gap-2">
          <HelpCircle size={13} className="text-slate-400 mt-0.5 flex-shrink-0" />
          <div>
            Enter your CHUB agent credentials (same ID/password you use to log into your TeleCMI softphone).
            When you click 📞 in a chat, CHUB rings your softphone first — pick it up and the customer dials automatically.
            Get your credentials from <a href="https://connle.telecmi.com" target="_blank" rel="noreferrer" className="text-emerald-600 underline">connle.telecmi.com → Settings → Agents</a>.
          </div>
        </div>
      </div>

      {/* Current user's form */}
      <div className="space-y-3">
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">CHUB Agent ID</label>
          <input
            type="text"
            value={chubId}
            onChange={(e) => setChubId(e.target.value)}
            placeholder="e.g. 103_1111113"
            className="w-full px-3 py-2 text-[12px] border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-200 outline-none"
            data-testid="chub-id-input"
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">CHUB Password</label>
          <input
            type="password"
            value={chubPassword}
            onChange={(e) => setChubPassword(e.target.value)}
            placeholder={me?.configured ? "•••••••• (already saved — re-enter to update)" : "Your CHUB agent password"}
            className="w-full px-3 py-2 text-[12px] border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-200 outline-none"
            data-testid="chub-password-input"
          />
        </div>

        {me?.configured && me?.name && (
          <div className="text-[11px] text-slate-600">
            Linked to: <span className="font-semibold text-slate-800">{me.name}</span>
            {me.inet_no ? <span className="text-slate-400"> · App ID {me.inet_no}</span> : null}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <button
            onClick={onSave}
            disabled={saving || !chubId.trim() || !chubPassword}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="chub-save-btn"
          >
            <Save size={12} /> {saving ? "Saving…" : me?.configured ? "Update credentials" : "Save & verify"}
          </button>
          {me?.configured && (
            <>
              <button
                onClick={onVerify}
                disabled={verifying}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50"
                data-testid="chub-verify-btn"
              >
                <RefreshCw size={12} className={verifying ? "animate-spin" : ""} /> Re-verify
              </button>
              <button
                onClick={onDelete}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-lg border border-red-200 text-red-600 hover:bg-red-50"
                data-testid="chub-delete-btn"
              >
                <Trash2 size={12} /> Remove
              </button>
            </>
          )}
        </div>
      </div>

      {/* Admin team view */}
      {isAdmin && adminList && (
        <div className="mt-6 pt-4 border-t border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <div className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Team · CHUB Configuration</div>
            <span className="text-[10px] text-slate-400">{adminList.length} configured</span>
          </div>
          {adminList.length === 0 ? (
            <div className="text-[11px] text-slate-400 italic">No agents configured yet. Each agent enters their own credentials above.</div>
          ) : (
            <div className="space-y-1">
              {adminList.map((a) => (
                <div
                  key={a.user_id}
                  className="flex items-center justify-between px-3 py-2 rounded-lg bg-slate-50 border border-slate-100 text-[11px]"
                  data-testid={`chub-admin-row-${a.user_id}`}
                >
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={12} className="text-emerald-500" />
                    <span className="font-semibold text-slate-800">{a.name || a.chub_id}</span>
                    <span className="text-slate-400">· {a.chub_id}</span>
                  </div>
                  <span className="text-[10px] text-slate-400">
                    {a.verified_at ? `verified ${a.verified_at.slice(0, 10)}` : ""}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TelecmiChubCard;
